import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { SITE_LOGO } from '../../constants/assets';
import { motion } from 'framer-motion';
import { useSiteSettings } from '../../context/SiteSettingsContext';

const JSPDF_SCRIPT = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
const HTML2CANVAS_SCRIPT = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";

interface AdminSettings {
  legal_name: string;
  tax_id: string;
  address: string;
  city: string;
  postal_code: string;
  phone: string;
  bank_name: string;
  bank_iban: string;
  bank_bic: string;
}

interface Invoice {
  id: string;
  partner_id: string;
  invoice_number?: number;
  total_amount: number;
  period_month: number;
  period_year: number;
  status: string;
  created_at: string;
  partners: {
    name: string;
    billing_details: {
      legal_name: string;
      tax_id: string;
      address: string;
      city: string;
      postal_code: string;
      po_number?: string;
    }
  }
}

interface Alteration {
  id: string;
  client_name: string;
  service_name: string;
  price: number;
  created_at: string;
}

export default function InvoiceView() {
  const { id } = useParams<{ id: string }>();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [alterations, setAlterations] = useState<Alteration[]>([]);
  const [adminSettings, setAdminSettings] = useState<AdminSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [libsReady, setLibsReady] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const { stripe: stripeConfig } = useSiteSettings();
  const invoiceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchData();
    const loadScript = (src: string, id: string) => {
      return new Promise((resolve) => {
        if (document.getElementById(id)) { resolve(true); return; }
        const script = document.createElement('script');
        script.id = id;
        script.src = src;
        script.onload = () => resolve(true);
        document.body.appendChild(script);
      });
    };
    const init = async () => {
      await loadScript(HTML2CANVAS_SCRIPT, 'canvas-lib');
      await loadScript(JSPDF_SCRIPT, 'pdf-lib');
      setLibsReady(true);
    };
    init();
  }, [id]);

  const fetchData = async () => {
    // 1. Fetch Invoice
    const { data: inv } = await supabase.from('partner_invoices').select('*, partners(*)').eq('id', id).single();
    if (inv) {
      setInvoice(inv);
      // 2. Fetch Alterations
      const { data: alts } = await supabase.from('alterations').select('*').eq('invoice_id', id);
      if (alts) setAlterations(alts);
    }

    // 3. Fetch Admin Settings
    const { data: settings } = await supabase.from('admin_settings').select('*').eq('id', 'invoice_details').maybeSingle();
    if (settings) {
      setAdminSettings(settings);
    }

    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    if (!invoiceRef.current || !libsReady || pdfGenerating) return;
    setPdfGenerating(true);
    try {
      const element = invoiceRef.current;
      const { jsPDF } = (window as any).jspdf;

      // A4 dimensions in mm
      const PAGE_W_MM = 210;
      const PAGE_H_MM = 297;

      // Render at a resolution that stays sharp for text/print without
      // producing an oversized bitmap. Cap by devicePixelRatio so we don't
      // needlessly upscale on standard displays.
      const SCALE = Math.min(2, window.devicePixelRatio || 1.5) || 2;
      const canvas = await (window as any).html2canvas(element, {
        scale: SCALE,
        useCORS: true,
        backgroundColor: '#FFFFFF',
        logging: false,
      });

      // How many px correspond to one A4 page height on the canvas
      const pageHeightPx = Math.floor((canvas.width * PAGE_H_MM) / PAGE_W_MM);
      const totalPages = Math.ceil(canvas.height / pageHeightPx);

      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });

      for (let page = 0; page < totalPages; page++) {
        if (page > 0) pdf.addPage();

        // Slice the source canvas vertically for this page
        const srcY = page * pageHeightPx;
        const srcH = Math.min(pageHeightPx, canvas.height - srcY);

        // Create a temporary canvas for this slice
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = pageHeightPx; // always full page height (blank if last page)
        const ctx = pageCanvas.getContext('2d')!;
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);

        // JPEG (quality 0.9) is dramatically smaller than PNG for a mostly
        // white document, and jsPDF compresses it further.
        const imgData = pageCanvas.toDataURL('image/jpeg', 0.9);
        pdf.addImage(imgData, 'JPEG', 0, 0, PAGE_W_MM, PAGE_H_MM, undefined, 'FAST');
      }

      pdf.save(`Factura_${invoice?.partners.name}_${invoice?.invoice_number || invoice?.id.split('-')[0]}.pdf`);
    } catch (error) {
      console.error(error);
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleStripePayment = async () => {
    if (!invoice || paymentLoading) return;
    setPaymentLoading(true);
    setPaymentError(null);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-invoice-checkout', {
        body: {
          invoice_id: invoice.id,
          origin_url: window.location.href,
          apply_surcharge: true,
        },
      });

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No se recibió la URL de pago');
      }
    } catch (err: any) {
      console.error('Error initiating payment (full object):', err);
      // Log more details if it's a Supabase error
      if (err.context) console.error('Error context:', err.context);
      setPaymentError('Hubo un error al conectar con la pasarela de pago. Por favor, contacta con el atelier.');
    } finally {
      setPaymentLoading(false);
    }
  };

  if (loading || !invoice) return null;

  const subtotal = invoice.total_amount;
  const vat = subtotal * 0.21;
  const grand_total = subtotal + vat;

  // Fallback settings if DB is empty
  const displaySettings = adminSettings || {
    legal_name: 'Anthony Sánchez Atelier',
    tax_id: '46146123Z',
    address: 'Mestre Nicolau, 19',
    city: 'Barcelona',
    postal_code: '08021',
    phone: '+34 932 000 000',
    bank_name: 'BANCO SABADELL',
    bank_iban: 'ES21 0081 1234 5678 9012 3456',
    bank_bic: 'BSABESBBXXX'
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] py-12 px-6 font-sans">
      <div className="max-w-[900px] mx-auto">
        {/* Elegant Action Bar */}
        <div className="flex justify-between items-center mb-8 no-print border-b border-brand-gray-100 pb-6">
          <div className="flex items-center gap-6">
            <button onClick={() => window.history.back()} className="text-brand-gray-400 hover:text-black transition-colors">
              <i className="ri-arrow-left-line text-xl" />
            </button>
            <span className="text-[10px] uppercase tracking-[0.3em] text-brand-gray-400 font-medium">Portal de Facturación Digital</span>
          </div>
          <div className="flex gap-4">
            <button onClick={() => window.print()} className="text-[10px] uppercase tracking-[0.2em] px-6 py-2 border border-brand-gray-200 rounded-full hover:bg-brand-gray-50 transition-all">Imprimir</button>
            <button onClick={handleDownloadPDF} disabled={pdfGenerating} className="text-[10px] uppercase tracking-[0.2em] px-8 py-2 bg-black text-white rounded-full hover:bg-brand-gray-800 transition-all disabled:opacity-30">
              {pdfGenerating ? 'Procesando...' : 'Descargar PDF'}
            </button>
          </div>
        </div>

        {/* COMPACT MINIMALIST LUXURY INVOICE */}
        <div className="w-full overflow-x-auto pb-8 -mx-6 px-6 md:mx-0 md:px-0 scrollbar-hide">
          <div 
            ref={invoiceRef} 
            className="bg-white relative p-12 md:p-16 border border-brand-gray-50 shadow-2xl mx-auto"
            style={{ width: '840px', minWidth: '840px', minHeight: '1100px' }}
          >
          {/* Header Section - Compact */}
          <div className="flex justify-between items-start mb-12 relative z-10 text-left">
            <div className="space-y-8">
              <img 
                src={SITE_LOGO} 
                alt="Anthony Sánchez" className="h-8 w-auto opacity-90"
              />
              <div className="space-y-0.5 text-[8px] uppercase tracking-[0.15em] text-brand-gray-400 font-medium">
                <p className="text-black font-bold">{displaySettings.legal_name}</p>
                <p>{displaySettings.address}, {displaySettings.postal_code} {displaySettings.city}</p>
                <p>NIF: {displaySettings.tax_id} <span className="mx-1">|</span> Tel: <span style={{ textDecoration: 'none' }} suppressHydrationWarning>{displaySettings.phone}</span></p>
              </div>
            </div>
            <div className="text-right space-y-8">
              <div className="space-y-1">
                <h1 className="text-[9px] uppercase tracking-[0.4em] text-brand-gray-300 font-light">Factura Oficial</h1>
                <p className="text-lg font-light tracking-[0.05em] text-black italic font-serif">Nº {invoice.invoice_number ? invoice.invoice_number.toString().padStart(4, '0') : invoice.id.split('-')[0].toUpperCase()}</p>
              </div>
              <div className="text-[8px] uppercase tracking-[0.15em] text-brand-gray-400 space-y-0.5">
                <p>Fecha de emisión</p>
                <p className="text-black font-bold">{new Date(invoice.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          {/* Partner & Period Info - Compact */}
          <div className="grid grid-cols-2 gap-12 mb-12 border-t border-brand-gray-100 pt-8 relative z-10 text-left">
            <div className="space-y-3">
              <span className="text-[7px] uppercase tracking-[0.3em] text-brand-gray-300 font-bold">Datos de Facturación</span>
              <div className="space-y-1">
                <p className="text-base font-serif italic text-black leading-tight">{invoice.partners.billing_details.legal_name}</p>
                <div className="space-y-0.5 text-[8px] uppercase tracking-[0.1em] text-brand-gray-500 font-medium">
                  <p>CIF/NIF: {invoice.partners.billing_details.tax_id}</p>
                  <p>{invoice.partners.billing_details.address}, {invoice.partners.billing_details.postal_code} {invoice.partners.billing_details.city}</p>
                </div>
                {invoice.partners.billing_details.po_number && (
                  <p className="text-base font-sans uppercase tracking-[0.1em] text-brand-gray-500 font-medium pt-1">{invoice.partners.billing_details.po_number}</p>
                )}
              </div>
            </div>
            <div className="text-right space-y-3">
              <span className="text-[7px] uppercase tracking-[0.3em] text-brand-gray-300 font-bold">Periodo de Servicio</span>
              <div className="space-y-0.5">
                <p className="text-xl font-serif font-light text-black capitalize tracking-tight">
                  {new Date(0, invoice.period_month-1).toLocaleString('es', { month: 'long' })} {invoice.period_year}
                </p>
                <p className="text-[8px] uppercase tracking-[0.2em] text-brand-sage font-medium italic">Atelier de Alta Costura</p>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="relative z-10">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-black">
                  <th className="pb-4 text-[7px] uppercase tracking-[0.3em] text-brand-gray-400 font-bold">Descripción</th>
                  <th className="pb-4 text-[7px] uppercase tracking-[0.3em] text-brand-gray-400 font-bold text-center w-16">Cant.</th>
                  <th className="pb-4 text-[7px] uppercase tracking-[0.3em] text-brand-gray-400 font-bold text-right w-24">Precio</th>
                  <th className="pb-4 text-[7px] uppercase tracking-[0.3em] text-brand-gray-400 font-bold text-right w-24">Total</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(alterations.reduce((acc: any, curr) => {
                  const keyName = curr.service_name === "Servicio Manual" ? curr.description : curr.service_name;
                  if (!acc[keyName]) acc[keyName] = { count: 0, total: 0, price: curr.price };
                  acc[keyName].count += 1;
                  acc[keyName].total += curr.price;
                  return acc;
                }, {})).map(([name, data]: [string, any]) => (
                  <tr key={name} className="border-b border-brand-gray-50">
                    <td className="py-4">
                      <p className="text-[11px] text-black font-medium tracking-wide">{name}</p>
                      <p className="text-[7px] uppercase tracking-[0.15em] text-brand-gray-300 mt-1 italic font-light font-serif text-left">Mano de obra artesanal</p>
                    </td>
                    <td className="py-4 text-[10px] text-brand-gray-400 text-center font-light">{data.count}</td>
                    <td className="py-4 text-[10px] text-brand-gray-400 text-right font-light">{data.price.toFixed(2)}€</td>
                    <td className="py-4 text-[12px] font-serif font-light text-black text-right tracking-tight">{data.total.toFixed(2)}€</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals & Payment - Dynamic */}
          <div className="mt-12 flex justify-between items-start gap-12 relative z-10 pt-10 border-t border-brand-gray-100 text-left">
            <div className="max-w-[280px] space-y-8">
              <div className="space-y-3">
                <span className="text-[7px] uppercase tracking-[0.3em] text-brand-gray-300 font-bold">Forma de Pago</span>
                <div className="p-4 border border-brand-gray-50 bg-[#FCFCFC] space-y-2 rounded-xl">
                  <p className="text-[8px] uppercase tracking-[0.15em] text-black font-bold">{displaySettings.bank_name}</p>
                  <p className="text-[9px] text-brand-gray-500 font-light tracking-widest break-all uppercase">{displaySettings.bank_iban}</p>
                  {displaySettings.bank_bic && <p className="text-[7px] text-brand-gray-400">BIC: {displaySettings.bank_bic}</p>}
                  <p className="text-[7px] uppercase tracking-[0.15em] text-brand-gray-300 italic mt-1">Ref: Factura {invoice.invoice_number ? invoice.invoice_number.toString().padStart(4, '0') : invoice.id.split('-')[0].toUpperCase()}</p>
                </div>
              </div>
            </div>
            
            <div className="w-56 space-y-3">
              <div className="flex justify-between text-[8px] uppercase tracking-[0.15em] text-brand-gray-400 font-medium">
                <span>Base Imponible</span>
                <span className="text-black">{subtotal.toFixed(2)}€</span>
              </div>
              <div className="flex justify-between text-[8px] uppercase tracking-[0.15em] text-brand-gray-400 font-medium pb-2 border-b border-brand-gray-50">
                <span>IVA (21%)</span>
                <span className="text-black">{vat.toFixed(2)}€</span>
              </div>
              <div className="flex justify-between items-end pt-2">
                <span className="text-[9px] uppercase tracking-[0.2em] text-brand-gray-300 italic font-serif">Total Factura</span>
                <span className="text-3xl font-serif font-light text-black tracking-tighter">{grand_total.toFixed(2)}€</span>
              </div>

              {stripeConfig.enabled && invoice.status !== 'paid' && (
                <div className="pt-10 space-y-5">
                  <div className="p-6 bg-brand-gray-50/50 rounded-[24px] border border-brand-gray-100 space-y-4">
                    <div className="flex justify-between items-baseline">
                      <span className="text-[8px] uppercase tracking-[0.2em] text-brand-gray-400 font-bold">Pago con Tarjeta</span>
                      <div className="text-right">
                        <span className="text-[10px] text-brand-gray-400 line-through mr-2">{grand_total.toFixed(2)}€</span>
                        <span className="text-sm font-serif italic text-black">{(grand_total * (1 + stripeConfig.invoiceSurchargePercentage / 100)).toFixed(2)}€</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 pt-4 border-t border-brand-gray-100/50">
                      <i className="ri-information-line text-brand-gray-300 text-sm mt-0.5" />
                      <p className="text-[9px] text-brand-gray-400 leading-relaxed italic font-serif">
                        La pasarela de pago seguro aplica un recargo del {stripeConfig.invoiceSurchargePercentage}% por gestión de cobro digital.
                      </p>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleStripePayment}
                    disabled={paymentLoading}
                    className="w-full h-16 bg-black text-white rounded-full font-sans text-[11px] uppercase tracking-[0.4em] font-bold hover:bg-brand-gray-800 transition-all shadow-2xl shadow-black/20 flex items-center justify-center relative overflow-hidden group disabled:opacity-50"
                  >
                    <div className="flex items-center justify-center gap-3 relative z-10">
                      <div className="flex items-center justify-center w-6 h-6">
                        {paymentLoading ? (
                          <i className="ri-loader-4-line animate-spin text-base" />
                        ) : (
                          <i className="ri-bank-card-line text-base" />
                        )}
                      </div>
                      <span className="tracking-[0.3em] text-[10px] translate-y-[0.5px]">
                        {paymentLoading ? 'PROCESANDO...' : 'PAGAR CON TARJETA'}
                      </span>
                    </div>
                  </button>

                  {paymentError && (
                    <motion.p 
                      initial={{ opacity: 0 }} 
                      animate={{ opacity: 1 }} 
                      className="text-[9px] text-red-500 text-center font-sans uppercase tracking-widest font-bold"
                    >
                      <i className="ri-error-warning-line mr-1" /> {paymentError}
                    </motion.p>
                  )}
                  
                  <p className="text-[7px] uppercase tracking-[0.3em] text-brand-gray-300 text-center">
                    Transacción segura encriptada vía Stripe™
                  </p>
                </div>
              )}

              {invoice.status === 'paid' && (
                <div className="pt-8">
                  <div className="flex items-center justify-center gap-2 p-4 bg-brand-sage/20 rounded-2xl border border-brand-sage/30 text-brand-black font-sans text-xs font-bold uppercase tracking-widest">
                    <i className="ri-checkbox-circle-fill text-lg" />
                    Factura Pagada
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Elegant Footer */}
          <div className="mt-24 text-center space-y-3 relative z-10 opacity-50">
            <div className="flex items-center justify-center gap-3">
              <div className="h-[0.5px] w-8 bg-brand-gray-200" />
              <p className="text-[8px] uppercase tracking-[0.4em] text-black font-light italic font-serif">Anthony Sánchez</p>
              <div className="h-[0.5px] w-8 bg-brand-gray-200" />
            </div>
            <p className="text-[7px] uppercase tracking-[0.3em] text-brand-gray-400 text-center">Atelier de Alta Costura Barcelona</p>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
